import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useRoles } from "@/hooks/useRoles";
import { useI18n } from "@/lib/i18n";
import LanguageSelector from "@/components/LanguageSelector";
import AccessibilityControls from "@/components/AccessibilityControls";
import { Menu, Heart, Map, User, LogOut, Plus, Shield, Bell, MessageCircle, BookOpen, HelpCircle, AlertTriangle, ClipboardList, Route, Settings, Download, Gamepad2, Sparkles, FolderKanban, Gift, Users, Bookmark } from "lucide-react";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { useUnreadCount } from "@/hooks/useNotifications";
import { useUnseenUpdatesCount } from "@/hooks/useUnseenUpdates";
import vantastikaLogo from "@/assets/vantastika-logo.svg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import HelpModal from "@/components/HelpModal";
import { lazy, Suspense } from "react";

const IncidentReportWizard = lazy(() => import("@/components/IncidentReportWizard"));
const KseniaChat = lazy(() => import("@/components/KseniaChat"));

/* ── Menu item component ── */
const MenuItem = ({ icon: Icon, label, onClick, destructive, accent }: {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  destructive?: boolean;
  accent?: boolean;
}) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-3.5 w-full px-4 py-3 rounded-2xl text-[0.9375rem] font-semibold transition-colors active:scale-[0.98] ${
      destructive
        ? "text-destructive active:bg-destructive/8"
        : accent
          ? "text-primary active:bg-primary/8"
          : "text-foreground active:bg-muted/60"
    }`}
  >
    <span className={`flex items-center justify-center w-9 h-9 rounded-xl ${
      destructive
        ? "bg-destructive/10"
        : "bg-primary/8"
    }`}>
      <Icon className={`h-[18px] w-[18px] ${destructive ? "text-destructive" : "text-primary"}`} />
    </span>
    {label}
  </button>
);

const MenuLinkItem = ({ icon: Icon, label, to, onClick }: {
  icon: React.ElementType;
  label: string;
  to: string;
  onClick: () => void;
}) => (
  <Link
    to={to}
    onClick={onClick}
    className="flex items-center gap-3.5 w-full px-4 py-3 rounded-2xl text-[0.9375rem] font-semibold text-foreground transition-colors active:bg-muted/60 active:scale-[0.98]"
  >
    <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary/8">
      <Icon className="h-[18px] w-[18px] text-primary" />
    </span>
    {label}
  </Link>
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground/40 px-4 pt-6 pb-2">
    {children}
  </p>
);

const SiteNavbar = () => {
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [vannieOpen, setVannieOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { isAdmin } = useRoles();
  const { canPrompt, isInstalled, isIOS, install } = useInstallPrompt();
  const [installInstructionsOpen, setInstallInstructionsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const unreadCount = useUnreadCount();
  const unseenUpdates = useUnseenUpdatesCount();

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const close = () => setOpen(false);

  const links = [
    { label: t("nav.trips"), href: "/trips" },
    ...(user ? [{ label: t("nav.bottom.myTrips"), href: "/my-trips" }] : []),
    { label: t("nav.explore"), href: "/search" },
    ...(user ? [{ label: t("nav.bottom.budget"), href: "/budget" }] : []),
    { label: t("nav.map"), href: "/map" },
  ];

  const visibleLinks = links;

  const isActive = (href: string) =>
    location.pathname === href || location.pathname.startsWith(href + "/");

  return (
    <>
      <nav
        className="sticky top-0 z-[55] bg-background/85 backdrop-blur-lg border-b border-border/60"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center group shrink-0">
            <img
              src={vantastikaLogo}
              alt="Vantastika"
              className="h-9 md:h-10 lg:h-11 w-auto object-contain group-hover:scale-[1.03] transition-transform"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {visibleLinks.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                className={`text-sm font-semibold px-3 py-2 rounded-lg transition-all ${
                  isActive(l.href)
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-primary/5"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </div>

          {/* Desktop right side */}
          <div className="hidden md:flex items-center gap-1.5 shrink-0">
            <AccessibilityControls />
            <div className="w-px h-5 bg-border/60 mx-1" />
            <LanguageSelector />

            {user ? (
              <>
                <Button variant="ghost" size="icon" className="relative rounded-full" onClick={() => navigate("/notifications")}>
                  <Bell className="h-4 w-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full border border-border/60">
                      <User className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate("/profile")}>
                      <User className="h-4 w-4 mr-2" /> {t("nav.profile")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/my-trips")}>
                      <Map className="h-4 w-4 mr-2" /> {t("nav.myTrips")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/favorites")}>
                      <Heart className="h-4 w-4 mr-2" /> {t("nav.favorites")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/wishlist")}>
                      <Bookmark className="h-4 w-4 mr-2" /> {t("wishlist.title")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/projects")}>
                      <FolderKanban className="h-4 w-4 mr-2" /> {t("projects.myProjects")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/checklists")}>
                      <ClipboardList className="h-4 w-4 mr-2" /> {t("nav.checklists")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/travel-guides")}>
                      <BookOpen className="h-4 w-4 mr-2" /> {t("nav.travelGuides")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/services")}>
                      <Settings className="h-4 w-4 mr-2" /> {t("nav.services")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/games")}>
                      <Gamepad2 className="h-4 w-4 mr-2" /> {t("nav.games")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/submit")}>
                      <Plus className="h-4 w-4 mr-2" /> {t("nav.submit")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setVannieOpen(true)}>
                      <Sparkles className="h-4 w-4 mr-2" /> Vannie
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setIncidentOpen(true)} className="text-destructive">
                      <AlertTriangle className="h-4 w-4 mr-2" /> {t("incident.button")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setHelpOpen(true)}>
                      <HelpCircle className="h-4 w-4 mr-2" /> {t("nav.help")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/contact")}>
                      <MessageCircle className="h-4 w-4 mr-2" /> {t("nav.contact")}
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => navigate("/admin")}>
                        <Shield className="h-4 w-4 mr-2" /> Admin
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => signOut()} className="text-destructive">
                      <LogOut className="h-4 w-4 mr-2" /> {t("nav.logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" className="font-semibold" onClick={() => navigate("/login")}>
                  {t("nav.login")}
                </Button>
                <Button size="sm" className="font-semibold shadow-md" onClick={() => navigate("/signup")}>
                  {t("nav.signup")}
                </Button>
              </div>
            )}
          </div>

          {/* Mobile right controls */}
          <div className="md:hidden flex items-center gap-0.5">
            <AccessibilityControls />
            <LanguageSelector />
            {user && (
              <button
                onClick={() => navigate("/notifications")}
                className="relative p-2.5 rounded-lg active:bg-muted/80 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={() => setOpen(!open)}
              className="relative p-2.5 rounded-xl active:bg-muted/80 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={open ? t("aria.closeMenu") : t("aria.openMenu")}
              aria-expanded={open}
            >
              <Menu className="h-5 w-5" />
              {unseenUpdates > 0 && (
                <span className="absolute top-1 right-1 flex h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-background" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* ── Modern mobile Sheet menu ── */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-[85vw] max-w-[340px] p-0 border-l-0 rounded-l-[2rem] bg-background/95 backdrop-blur-2xl shadow-2xl [&>button]:top-5 [&>button]:right-5 [&>button]:rounded-xl [&>button]:h-9 [&>button]:w-9 [&>button]:bg-muted/40"
        >
          <SheetHeader className="px-5 pt-6 pb-2">
            <SheetTitle className="text-left">
              <img src={vantastikaLogo} alt="Vantastika" className="h-8 w-auto" />
            </SheetTitle>
          </SheetHeader>

          <ScrollArea className="h-[calc(100dvh-5rem)]">
            <div className="px-2 pb-10">
              {user ? (
                <>
                  {/* ── Mina ── */}
                  <SectionLabel>{t("nav.menuMine")}</SectionLabel>
                  <div className="space-y-0.5">
                    <MenuLinkItem icon={Route} label={t("nav.bottom.myTrips")} to="/my-trips" onClick={close} />
                    <MenuLinkItem icon={ClipboardList} label={t("nav.checklists")} to="/checklists" onClick={close} />
                    <MenuLinkItem icon={Heart} label={t("nav.favorites")} to="/favorites" onClick={close} />
                    <MenuLinkItem icon={Bookmark} label={t("wishlist.title")} to="/wishlist" onClick={close} />
                    <MenuLinkItem icon={FolderKanban} label={t("projects.myProjects")} to="/projects" onClick={close} />
                  </div>

                  {/* ── Utforska ── */}
                  <SectionLabel>{t("nav.explore")}</SectionLabel>
                  <div className="space-y-0.5">
                    <MenuLinkItem icon={Map} label={t("nav.map")} to="/map" onClick={close} />
                    <MenuLinkItem icon={Users} label={t("nav.community")} to="/community" onClick={close} />
                    <MenuLinkItem icon={BookOpen} label={t("nav.travelGuides")} to="/travel-guides" onClick={close} />
                    <MenuLinkItem icon={Settings} label={t("nav.services")} to="/services" onClick={close} />
                    <MenuLinkItem icon={Gamepad2} label={t("nav.games")} to="/games" onClick={close} />
                  </div>

                  {/* ── Skapa / Bidra ── */}
                  <SectionLabel>{t("nav.menuCreate")}</SectionLabel>
                  <div className="space-y-0.5">
                    <MenuLinkItem icon={Plus} label={t("nav.submit")} to="/submit" onClick={close} />
                    <MenuItem icon={AlertTriangle} label={t("incident.button")} onClick={() => { setIncidentOpen(true); close(); }} destructive />
                  </div>

                  {/* ── App ── */}
                  <SectionLabel>{t("nav.menuApp")}</SectionLabel>
                  <div className="space-y-0.5">
                    <Link
                      to="/whats-new"
                      onClick={close}
                      className="flex items-center gap-3.5 w-full px-4 py-3 rounded-2xl text-[0.9375rem] font-semibold text-foreground transition-colors active:bg-muted/60 active:scale-[0.98]"
                    >
                      <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary/8">
                        <Gift className="h-[18px] w-[18px] text-primary" />
                      </span>
                      {t("whatsNew.title")}
                      {unseenUpdates > 0 && (
                        <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
                          {unseenUpdates > 9 ? "9+" : unseenUpdates}
                        </span>
                      )}
                    </Link>
                    <MenuItem icon={Sparkles} label={t("nav.vannie")} onClick={() => { setVannieOpen(true); close(); }} />
                    <MenuLinkItem icon={Settings} label={t("nav.settings")} to="/profile" onClick={close} />
                    <MenuLinkItem icon={MessageCircle} label={t("nav.contact")} to="/contact" onClick={close} />
                    <MenuItem icon={HelpCircle} label={t("nav.help")} onClick={() => { setHelpOpen(true); close(); }} />
                    {!isInstalled && (
                      <MenuItem
                        icon={Download}
                        label={t("nav.installApp")}
                        accent
                        onClick={async () => {
                          if (canPrompt) {
                            await install();
                            close();
                          } else {
                            close();
                            setInstallInstructionsOpen(true);
                          }
                        }}
                      />
                    )}
                  </div>

                  {isAdmin && (
                    <>
                      <SectionLabel>{t("nav.menuOther")}</SectionLabel>
                      <div className="space-y-0.5">
                        <MenuLinkItem icon={Shield} label="Admin" to="/admin" onClick={close} />
                      </div>
                    </>
                  )}

                  {/* Logout */}
                  <div className="mx-4 mt-6 mb-2">
                    <div className="h-px bg-border/40" />
                  </div>
                  <MenuItem icon={LogOut} label={t("nav.logout")} onClick={() => { signOut(); close(); }} destructive />
                </>
              ) : (
                <div className="flex flex-col gap-3 px-4 pt-6">
                  <Button className="w-full h-12 rounded-2xl font-semibold text-base" onClick={() => { navigate("/signup"); close(); }}>
                    {t("nav.signup")}
                  </Button>
                  <Button variant="outline" className="w-full h-12 rounded-2xl font-semibold text-base" onClick={() => { navigate("/login"); close(); }}>
                    {t("nav.login")}
                  </Button>
                </div>
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      <HelpModal open={helpOpen} onOpenChange={setHelpOpen} />
      {incidentOpen && user && (
        <Suspense fallback={null}>
          <IncidentReportWizard onClose={() => setIncidentOpen(false)} />
        </Suspense>
      )}
      {vannieOpen && (
        <Suspense fallback={null}>
          <KseniaChat open={vannieOpen} onOpenChange={setVannieOpen} />
        </Suspense>
      )}

      {/* Install instructions dialog (for iOS / non-chromium) */}
      {installInstructionsOpen && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setInstallInstructionsOpen(false)} />
          <div className="relative bg-card rounded-t-2xl sm:rounded-2xl w-full max-w-sm mx-auto p-6 pb-8 border border-border/50 shadow-2xl animate-in slide-in-from-bottom-4 duration-300">
            <button onClick={() => setInstallInstructionsOpen(false)} className="absolute top-3 right-3 p-1.5 rounded-lg text-muted-foreground hover:bg-muted/60">
              <Menu className="h-4 w-4" />
            </button>
             <h3 className="text-lg font-bold text-foreground mb-4">{t("nav.installTitle")}</h3>
            {isIOS ? (
              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">1</span>
                  <span><strong>{t("nav.installIOS1")}</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">2</span>
                  <span><strong>{t("nav.installIOS2")}</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">3</span>
                  <span><strong>{t("nav.installIOS3")}</strong></span>
                </li>
              </ol>
            ) : (
              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">1</span>
                  <span><strong>{t("nav.installAndroid1")}</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">2</span>
                  <span><strong>{t("nav.installAndroid2")}</strong></span>
                </li>
              </ol>
            )}
            <Button onClick={() => setInstallInstructionsOpen(false)} variant="outline" className="w-full mt-5 font-semibold">
              {t("nav.installUnderstood")}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default SiteNavbar;
